<template>
    <div class="border bg-gray-100 rounded faq-item cursor-move p-4 mt-3">
        <div class="mb-4">Reference List</div>

        <Select
            label="Type"
            name="type"
            v-model="form.type"
            class="mt-2 col-span-3"
            :options="typeOptions"
            @input="() => {
                // can't use watch because it will be triggered when setForm is called by mounted, then these will be null on load
                form.categories = [];
                form.casino_reviews = [];
                form.game_reviews = [];
                form.slot_reviews = [];
            }"
            :error="errorBag?.type ? errorBag.type[0] : null"
        />

        <input-text
            label="Number of visible items"
            name="number_of_visible_items"
            type="number"
            :max="16"
            v-model="form.number_of_visible_items"
            class="mt-2"
            :error="errorBag?.number_of_visible_items ? errorBag.number_of_visible_items[0] : null"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-2"
            v-if="form.type === 'software' || form.type === 'payment'"
        >
            <p class="text-gray-700 mb-4">Category Pages (Max. {{ maxItems }})</p>

            <multi-reference
                :link-text="`Link existing ${typeName}`"
                :linkable="true"
                :creatable="false"
                :language="language"
                :max-items="maxItems"
                v-model="form.categories"
                field-name="categories"
                :model-data="model"
                :should-open-new-window=true
                :query="{
                    status_id: 1
                }"
                :order="{
                    'name': 'asc'
                }"
                :withDocs="withDocs"
                :disabled="!model"
                :error="errorBag?.root ? errorBag.root[0] : null"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-2"
            v-if="form.type === 'casino'"
        >
            <p class="text-gray-700 mb-4">Casino Reviews (Max. {{ maxItems }})</p>

            <multi-reference
                :link-text="`Link existing Casino Reviews`"
                :linkable="true"
                :creatable="false"
                :language="language"
                :max-items="maxItems"
                v-model="form.casino_reviews"
                field-name="casino_reviews"
                :model-data="model"
                :should-open-new-window=true
                :query="{
                    status_id: 1
                }"
                :order="{
                    'name': 'asc'
                }"
                :withDocs="withDocs"
                :disabled="!model"
                :error="errorBag?.root ? errorBag.root[0] : null"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-2"
            v-if="form.type === 'game'"
        >
            <p class="text-gray-700 mb-4">Game Reviews (Max. {{ maxItems }})</p>

            <multi-reference
                :link-text="`Link existing Game Reviews`"
                :linkable="true"
                :creatable="false"
                :language="language"
                :max-items="maxItems"
                v-model="form.game_reviews"
                field-name="game_reviews"
                :model-data="model"
                :should-open-new-window=true
                :query="{
                    status_id: 1
                }"
                :order="{
                    'name': 'asc'
                }"
                :withDocs="withDocs"
                :disabled="!model"
                :error="errorBag?.root ? errorBag.root[0] : null"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-2"
            v-if="form.type === 'slot'"
        >
            <p class="text-gray-700 mb-4">Slot Reviews (Max. {{ maxItems }})</p>

            <multi-reference
                :link-text="`Link existing Slot Reviews`"
                :linkable="true"
                :creatable="false"
                :language="language"
                :max-items="maxItems"
                v-model="form.slot_reviews"
                field-name="slot_reviews"
                :model-data="model"
                :should-open-new-window=true
                :query="{
                    status_id: 1
                }"
                :order="{
                    'name': 'asc'
                }"
                :withDocs="withDocs"
                :disabled="!model"
                :error="errorBag?.root ? errorBag.root[0] : null"
            />
        </div>

    </div>
</template>

<script>
import draggable from "vuedraggable";
import siteLanguage from '../../config/siteLanguage';

const MAX_ITEMS = 16;
export default {
    components: { draggable },
    name: "ReferenceList",
    props: {
        value: {
            type: Object,
            required: false,
        },
        errorBag: {
            type: [Object, Array],
            required: false,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
    },
    computed: {
        maxItems () {
            return MAX_ITEMS;
        },
        typeName() {
            return this.typeOptions.filter(e => e.value === this.form.type)[0]?.name ?? null;
        },
        model() {
            switch(this.form.type) {
                case 'software': return 'Software Category';
                case 'payment': return 'Payment Category';
                case 'casino': return 'Casino Review';
                case 'slot': return 'Slot Review';
                case 'game': return 'Game Review';
                default: return null;
            }
        },
        withDocs() {
            switch(this.form.type) {
                case 'software': return ['software'];
                case 'payment': return ['paymentMethod'];
                case 'casino': return ['casino'];
                case 'slot': return ['slot'];
                case 'game': return ['game'];
                default: return [];
            }
        },
    },
    data(){
        return {
            form: {
                type: null,
                number_of_visible_items: null,
                categories: [],
                casino_reviews: [],
                game_reviews: [],
                slot_reviews: [],
            },
            typeOptions: [
                { value: 'software', name: 'Software Category' },
                { value: 'payment', name: 'Payment Category' },
                { value: 'casino', name: 'Casino Review' },
                { value: 'slot', name: 'Slot Review' },
                { value: 'game', name: 'Game Review'},
            ],
        }
    },
    mounted: function () {
        this.setForm();
    },
    methods: {
        setForm() {
            if (this.value && this.value.type) {
                this.form.type = this.value.type;
                this.form.number_of_visible_items = this.value.number_of_visible_items;
                this.form.categories = this.value.categories ?? [];
                this.form.casino_reviews = this.value.casino_reviews ?? [];
                this.form.game_reviews = this.value.game_reviews ?? [];
                this.form.slot_reviews = this.value.slot_reviews ?? [];
            }
        }
    },
    watch: {
        value: {
            deep: true,
            handler: function (newValue) {
                this.setForm();
            },
        },
        form: {
            deep: true,
            handler: function (newValue) {
                this.$emit('input', newValue);
            },
        },
    },
};
</script>
